import { useNavigate } from "react-router-dom";
import { ArrowRight, Video, HeartPulse, MapPin, Moon, Sun, Plus, Sprout } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { useTheme } from "@/context/ThemeContext";
import heroBg from "@/assets/pharma-hero.jpg";

const PH = "pharmacie-des-ecoles";
const haloDark = { textShadow: "0 2px 22px rgba(255,255,255,0.92), 0 1px 3px rgba(255,255,255,0.85)" };

const GlassCard = ({ testid, onClick, accent, icon: Icon, title, desc }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="group relative text-center w-full rounded-3xl border border-white/60 bg-white/35 backdrop-blur-xl px-6 pt-14 pb-7 shadow-[0_20px_45px_-12px_rgba(16,24,40,0.5)] hover:bg-white/55 hover:-translate-y-1.5 active:scale-[0.98] transition-all duration-300"
  >
    <span
      className="absolute -top-9 left-1/2 -translate-x-1/2 h-20 w-20 rounded-full grid place-items-center text-white group-hover:scale-110 transition-transform duration-300"
      style={{ backgroundColor: accent, boxShadow: `0 0 38px 10px ${accent}CC, 0 12px 26px -6px ${accent}` }}
    >
      <Icon className="h-9 w-9" strokeWidth={2.2} />
    </span>
    <h2 className="font-['Outfit'] text-xl font-extrabold leading-snug text-slate-900 mb-2" style={haloDark}>{title}</h2>
    <p className="text-base font-medium text-slate-800 leading-relaxed mb-6" style={haloDark}>{desc}</p>
    <span
      className="mx-auto h-14 w-14 rounded-full grid place-items-center text-white group-hover:scale-110 transition-transform duration-300"
      style={{ backgroundColor: accent, boxShadow: `0 0 24px 5px ${accent}AA` }}
    >
      <ArrowRight className="h-7 w-7" strokeWidth={2.4} />
    </span>
  </button>
);

export default function HomeHub() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <div className="min-h-screen relative flex flex-col">
      {/* Fond saturé couvrant toute la page, jusqu'en bas */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroBg})`, filter: "saturate(1.22) contrast(1.05) brightness(1.02)" }}
      />
      <div className={`absolute inset-0 ${dark ? "bg-slate-950/50" : "bg-gradient-to-b from-white/25 via-transparent to-white/25"}`} />

      <section className="relative z-10 flex-1 flex flex-col">
        {/* Barre haute : mode nuit + logo EYMI */}
        <div className="relative z-10 flex items-center justify-between px-5 pt-5">
          <button
            data-testid="home-theme-toggle"
            onClick={toggle}
            aria-label="Mode nuit"
            className="h-10 w-10 grid place-items-center rounded-full border border-white/60 bg-white/50 backdrop-blur text-slate-700 hover:bg-white/75 shadow-sm transition-colors"
          >
            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <div className="flex items-center gap-1.5 text-emerald-700 bg-white/45 backdrop-blur rounded-full px-3 py-1 shadow-sm">
            <Sprout className="h-6 w-6" />
            <span className="font-['Outfit'] font-extrabold text-xl tracking-wide">EYMI</span>
          </div>
        </div>

        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 py-10 max-w-5xl mx-auto w-full animate-fade-up">
          {/* Accueil translucide, bien visible */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2.5 mb-1.5">
              <span className="h-11 w-11 rounded-lg bg-emerald-600 grid place-items-center text-white shadow-md"><Plus className="h-7 w-7" strokeWidth={3} /></span>
              <span className="font-['Outfit'] font-bold text-xl sm:text-2xl tracking-[0.16em] text-emerald-800" style={haloDark}>PHARMACIE</span>
            </div>
            <p className="font-['Outfit'] text-base font-semibold tracking-[0.22em] text-emerald-700" style={haloDark}>DES ÉCOLES</p>
            <p className="text-xs tracking-[0.28em] text-slate-700 mt-1 mb-4" style={haloDark}>LA LONDE-LES-MAURES</p>
            <h1 className="font-['Outfit'] text-7xl sm:text-8xl font-extrabold tracking-tight text-slate-900 leading-none" style={haloDark}>Bonjour</h1>
            <p className="text-3xl sm:text-5xl text-slate-900 font-extrabold mt-4 leading-tight" style={haloDark}>Comment pouvons-nous<br /> vous aider aujourd'hui ?</p>
          </div>

          {/* Cartes */}
          <div className="grid sm:grid-cols-3 gap-10 sm:gap-6 w-full px-1">
            <GlassCard
              testid="hub-teleconsultation"
              onClick={() => navigate(`/p/${PH}/teleconsultation-specialiste`)}
              accent="#10B981"
              icon={Video}
              title="Prendre rendez-vous pour une téléconsultation"
              desc="Dans la pharmacie avec un médecin spécialiste"
            />
            <GlassCard
              testid="hub-bilan"
              onClick={() => navigate(`/p/${PH}/bilan-prevention`)}
              accent="#8B5CF6"
              icon={HeartPulse}
              title="Réaliser mon bilan de prévention"
              desc="Conseils personnalisés avec votre pharmacien"
            />
            <GlassCard
              testid="hub-instapharm"
              onClick={() => navigate(`/p/${PH}/services-urgences`)}
              accent="#3B82F6"
              icon={MapPin}
              title="Trouver une solution médicale près de moi"
              desc="Médecins, centres de soins et urgences dans le Var"
            />
          </div>

          <p className="text-center text-sm text-red-700 font-bold mt-12 bg-white/70 backdrop-blur rounded-full px-5 py-2.5 shadow-sm">
            En cas d'urgence vitale, appelez le <span className="font-['JetBrains_Mono'] font-bold">15</span> ou le <span className="font-['JetBrains_Mono'] font-bold">112</span> (sourds/malentendants : <span className="font-['JetBrains_Mono'] font-bold">114</span>).
          </p>
          <p className="text-center mt-5">
            <button data-testid="hub-staff" onClick={() => navigate("/login")} className="text-sm text-slate-800 hover:text-slate-900 underline underline-offset-2 bg-white/55 rounded-full px-3 py-1 transition-colors">
              Vous faites partie du personnel ? Se connecter
            </button>
          </p>
        </div>
      </section>
      <div className="relative z-10 bg-white/55 backdrop-blur border-t border-white/40">
        <SiteFooter />
      </div>
    </div>
  );
}
